import type { LocationQuery, RouteLocationRaw } from 'vue-router';
import { RouteNames } from '@/router/routeNames';

/**
 * Inbound link contract of the portal root ("/").
 *
 * These URLs are printed on QR codes, sent by the mobile app and built for payment
 * providers, so they MUST keep working exactly as in the Vue 2 portal:
 *
 *   /?ma=<mobileHash>                          mobile app registration
 *   /?id=<mediaHash>                           QR (mykey) landing
 *   /?action=recharge&serial=..&deviceId=..&type=..   open recharge for a key (logged in)
 *   /?checkout=true&serial=..&messaggio=..     Nexi return URL (`url`)
 *   /?action=recharge&type=2&serial=..         Nexi cancel URL (`url_back`)
 *
 * Parameter names are matched as given or fully upper-cased (e.g. `ID`), like before.
 */
export const EntryParams = {
  MobileHash: 'ma',
  MediaHash: 'id',
  Action: 'action',
  MediaType: 'type',
  MediaSerial: 'serial',
  DeviceId: 'deviceId',
  Checkout: 'checkout',
  CheckoutMessage: 'messaggio',
} as const;

export const EntryActions = {
  Recharge: 'recharge',
} as const;

/** Query the Customer route understands (all optional strings). */
export interface CustomerEntryQuery {
  action?: string;
  serial?: string;
  deviceId?: string;
  type?: string;
  checkout?: string;
  messaggio?: string;
}

export function readParam(query: LocationQuery, name: string): string | undefined {
  for (const key of [name, name.toUpperCase()]) {
    const raw = query[key];
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  return undefined;
}

function compact<T extends Record<string, string | undefined>>(obj: T): Record<string, string> {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Record<string, string>;
}

/**
 * Decides where a hit on "/" should go. Returns null to stay on the home page.
 */
export function resolveEntryLink(query: LocationQuery, isAuthenticated: boolean): RouteLocationRaw | null {
  const mobileHash = readParam(query, EntryParams.MobileHash);
  if (mobileHash) {
    return { name: RouteNames.MobileAppRegistration, query: { ma: mobileHash } };
  }

  const mediaHash = readParam(query, EntryParams.MediaHash);
  if (mediaHash) {
    return { name: RouteNames.QrCheck, query: { id: mediaHash } };
  }

  if (!isAuthenticated) {
    return null;
  }

  const customerQuery: CustomerEntryQuery = {
    action: readParam(query, EntryParams.Action),
    serial: readParam(query, EntryParams.MediaSerial),
    deviceId: readParam(query, EntryParams.DeviceId),
    type: readParam(query, EntryParams.MediaType),
    checkout: readParam(query, EntryParams.Checkout),
    messaggio: readParam(query, EntryParams.CheckoutMessage),
  };
  return { name: RouteNames.Customer, query: compact({ ...customerQuery }) };
}
